import {
  DashboardState,
  ESdChangeReason,
  IDisplayItem,
  IDisplayItemProperty,
  IScreenDesign,
  IScreenDesignPublicJSON,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { copyTextToClipboard } from "../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";

export interface IToolbarSaveSettingsState {}

export interface IToolbarSaveSettingsProps {}

class DevTools extends PureSdComponent<
  IToolbarSaveSettingsProps & IWithSDProps & IWithContextProp,
  IToolbarSaveSettingsState
> {
  constructor(props: IToolbarSaveSettingsProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  /**
   * The one copy every item here makes, through the designer's clipboard helper — it falls back
   * when the Clipboard API is refused, where a bare `writeText` rejected and said nothing. The menu
   * closes on the click, so there is no control left to roll a ✓ on: the toast is the only place
   * the copy can be confirmed, and now it only says "copied" when something was.
   */
  private copy = async (text: string, what: string): Promise<void> => {
    const succeeded: boolean = await copyTextToClipboard(text);
    if (succeeded) {
      KMainFunctions.displaySuccessNotification(`${what} copied to clipboard`);
      return;
    }
    KMainFunctions.displayErrorNotification(`${what} could not be copied to the clipboard`);
  };

  onClickCopyScreenToClipboard = async () => {
    const screen: IScreenDesign = this.props.sd.getScreenDesign();
    const screenJson: string = JSON.stringify(screen, null, 0);
    await this.copy(screenJson, "Screen");
  };

  onClickCopyPublicJsonToClipboard = async () => {
    const screenInputs = this.props.sd.getScreenInputs();
    const screenOutputs = this.props.sd.getScreenOutputs();
    const screenType = this.props.sd.getScreenType();
    const masterId = this.props.sd.masterSd?.id;
    const publicJsonModel: IScreenDesignPublicJSON = {
      screenInputs,
      screenOutputs,
      screenType,
      masterId
    };
    const publicJson: string = JSON.stringify(publicJsonModel, null, 0);
    await this.copy(publicJson, "Public json");
  };

  onClickCopySelectedDisplayItemToClipboard = async () => {
    const screen: IScreenDesign = this.props.sd.getScreenDesign();
    const selectedDi: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(screen);

    if (selectedDi && selectedDi.id !== SpecialDIIds.rootDI) {
      const selectedDiJson: string = JSON.stringify(selectedDi, null, 0);
      await this.copy(selectedDiJson, "Selected display item");
    }
  };

  onClickCopyFormMetadataToClipboard = async () => {
    const screen: IScreenDesign = this.props.sd.getScreenDesign();
    const selectedDi: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(screen);

    if (selectedDi?.item.name !== "Form") return;

    const metadataProp: IDisplayItemProperty | undefined = selectedDi.item.properties.find(
      (prop: IDisplayItemProperty) => prop.name === "metadata"
    );

    if (!metadataProp) return;
    await this.copy(metadataProp.symbol.fixedValue, "Form metadata");
  };

  getMenuOptions = (): KuikaDropdownMenuItemType[] => {
    const screen: IScreenDesign = this.props.sd.getScreenDesign();
    const selectedDi: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(screen);

    return [
      {
        key: "copy-screen",
        label: "Copy screen to clipboard",
        onClick: this.onClickCopyScreenToClipboard
      },
      {
        key: "copy-public-json",
        label: "Copy public json to clipboard",
        onClick: this.onClickCopyPublicJsonToClipboard
      },
      {
        key: "copy-selected-di",
        label: "Copy selected display item to clipboard",
        hidden: !selectedDi || selectedDi.id === SpecialDIIds.rootDI,
        onClick: this.onClickCopySelectedDisplayItemToClipboard
      },
      {
        key: "copy-form-metadata",
        label: "Copy form metadata to clipboard",
        hidden: selectedDi?.item.name !== "Form",
        onClick: this.onClickCopyFormMetadataToClipboard
      }
    ];
  };

  render() {
    return (
      <>
        <div
          style={{
            display: "flex"
          }}
        >
          {this.props.sd.dashboardState !== DashboardState.workFlow &&
            this.props.sd.dashboardState !== DashboardState.apiManagement &&
            this.props.sd.dashboardState !== DashboardState.configManager &&
            this.props.sd.dashboardState !== DashboardState.dataDiagramScheme &&
            this.props.sd.dashboardState !== DashboardState.scheduledJobs &&
            this.props.sd.dashboardState !== DashboardState.notificationTemplate &&
            this.props.sd.dashboardState !== DashboardState.zendesk && (
              <>
                <KuikaTooltip content="Dev Tools">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#818c99"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="feather feather-tool"
                    style={{ alignSelf: "center" }}
                  >
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                  </svg>
                </KuikaTooltip>
                <div>
                  <KuikaDropdownMenu options={this.getMenuOptions()} trigger="click" placement="bottomRight">
                    <div
                      style={{
                        height: "24px",
                        cursor: "pointer",
                        width: "18px"
                      }}
                    >
                      <span>
                        <svg
                          className="kuika_hover_save_icon kuika_leftMenuItemSvg"
                          style={{ marginBottom: "-3px" }}
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                        >
                          <defs>
                            <filter id="rqygt82eza">
                              <feColorMatrix
                                in="SourceGraphic"
                                values="0 0 0 0 0.504000 0 0 0 0 0.550345 0 0 0 0 0.600000 0 0 0 1.000000 0"
                              />
                            </filter>
                          </defs>
                          <g fill="none" fillRule="evenodd">
                            <g>
                              <path d="M0 0H12V12H0z" />
                              <path
                                fill="#818c99"
                                fillRule="nonzero"
                                d="M5.46 3.4H.54c-.195 0-.37-.064-.467-.17-.098-.107-.098-.234 0-.34L2.532.207C2.63.1 2.805.038 3 .038s.37.063.468.17l2.46 2.682c.097.106.097.233 0 .34-.099.106-.274.17-.469.17z"
                                transform="rotate(-180 4.5 4)"
                              />
                            </g>
                          </g>
                        </svg>
                      </span>
                    </div>
                  </KuikaDropdownMenu>
                </div>
              </>
            )}
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools
    );
  };
}

const devTools = withContext<IToolbarSaveSettingsProps>(withSD<IToolbarSaveSettingsProps>(DevTools));
export { devTools as DevTools };
